import { Formik, getIn } from 'formik'
import React, { useState } from 'react'
import { useApolloClient, useMutation } from '@apollo/client/react'
import { Button } from '../../../../components/Button'
import { BigButtonSection, RouteColumn, RouteRow, RouteWrapper } from '../../../../components/layout'
import { setErrorNotification, setSuccessNotification } from '../../../../components/notification'
import GetGeolocation from '../../../../components/Geolocation'
import { GeolocationType } from '../../../../components/Geolocation/types'
import { Loading } from '../../../../components/responses'
import { setErrorNotifications } from '../../../../components/responses/setErrorNotifications'
import { useHistoryPush } from '../../../../components/router'
import { Styling } from '../../../../components/Styling'
import { SetTowingRecordType, TowingRecord } from '../../ViewTowingRecord/types'
import { RouteHeader } from '../routes/subforms/RouteHeader'
import { RouteForm } from '../routes/subforms/styled'
import { messages } from '../../messages'
import { messages as cancelMessage } from './messages'
import { CANCEL_TOWING_RECORD } from './mutation/mutation'
import { toCancelMutationVariables } from './mutation/toMutationVariables'
import { toMutationVariables } from '../../ViewTowingRecord/mutation/toMutationVariables'
import { CancelTowingRecordResponse } from './mutation/types'
import { CancelOrEndTowingRecordFormValues } from './types'
import { maybeStopTrackingRecord } from '../../../../components/TrackedJobs/util'
import { EndJobResponse } from '../../ViewTowingRecord/mutation/types'
import { END_TOWING_RECORD } from '../../EditTowingRecord/mutation/mutation'
import { format } from 'date-fns'
import { DateTimeInput } from '../../../../components/layout/form/input/DateTimeInput'
import { Maybe, Time, TowingRecordType } from '../../../../common/types'
import { TextAreaInput } from '../../../../components/layout/form/input'
import { LocationInputMap, toMapData } from '../../../../components/layout/form/input/LocationInputMap'
import { ButtonContainer } from '../routes/subforms/styled'
import { formatMessageSafe } from '../../../../util/translateInputOptions'
import { useIntl } from 'react-intl'
import { GeoLocateAddressData } from '../routes/types'
import { useSettings } from '../../../Parameters'
import { isPaymentLinkAlreadyActive } from '../billing/TowingRecordBilling/types'

type ActionButtons = Maybe<
  'stopAndReady' | 'stopAndCard' | 'stopAndCash' | 'stopAndCancel' | 'stopAndModify' | 'stopAndPaymentLink'
>

const EDIT_TOWING_RECORD_BUTTONS: ActionButtons[] = [
  'stopAndCard',
  'stopAndCash',
  'stopAndModify',
  'stopAndPaymentLink',
]

interface CancelOrEndTowingRecordProps {
  onCancel: () => void
  towingRecord: TowingRecord
  setTowingRecord?: SetTowingRecordType
  subFormType: 'end' | 'cancel'
}

const CancelOrEndTowingRecordIntl: React.FunctionComponent<CancelOrEndTowingRecordProps> = ({
  onCancel,
  towingRecord,
  setTowingRecord,
  subFormType,
}) => {
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const settings = useSettings()
  const historyPush = useHistoryPush()
  const client = useApolloClient()
  const [path, setPath] = useState<string>(`/towing-record/${towingRecord.id}`)
  const [selectedButton, setSelectedButton] = useState<ActionButtons>(null)

  const paymentLinkEnabled = settings?.companyData?.paymentLinkEnabled ?? false
  const billingStatus = towingRecord.billing?.[0]?.status
  const paymentLinkAlreadyActive = isPaymentLinkAlreadyActive(towingRecord.paymentType, billingStatus)

  const [endJob, endQuery] = useMutation<EndJobResponse>(END_TOWING_RECORD, {
    onCompleted: data => {
      const { endJob } = data
      if (endJob.__typename === 'EndJobSuccess') {
        maybeStopTrackingRecord(endJob.towingRecord)
        setSuccessNotification(
          formatMessage(messages.job_end_success_title),
          formatMessage(messages.job_end_success_message)
        )

        if (!path.includes('edit') && setTowingRecord) {
          setTowingRecord(endJob.towingRecord, 'view-towing-record')
          return
        }

        historyPush(path)

        return
      }
      setErrorNotifications({ data: endJob })
    },
  })

  const getUrl = (selectedButton: ActionButtons) => {
    const editTowingRecord = selectedButton !== null && EDIT_TOWING_RECORD_BUTTONS.includes(selectedButton)
    let url = editTowingRecord ? `/towing-record/${towingRecord.id}/edit` : `/towing-record/${towingRecord.id}`

    if (selectedButton === 'stopAndCard') {
      url += '/payment/card'
    }
    if (selectedButton === 'stopAndCash') {
      url += '/payment/cash'
    }
    if (selectedButton === 'stopAndPaymentLink') {
      url += '/payment/online_payment'
    }

    return url
  }

  const finishJob = async (values: CancelOrEndTowingRecordFormValues, selectedButton: ActionButtons) => {
    const readyForPayment = selectedButton === 'stopAndReady' && !paymentLinkAlreadyActive

    const newPath = getUrl(selectedButton)
    if (path !== newPath) {
      setPath(newPath)
    }

    if (subFormType === 'end') {
      endJob({ variables: toMutationVariables(towingRecord, readyForPayment, values) })
      return
    }

    if (subFormType === 'cancel') {
      await cancelTowingRecord({ variables: toCancelMutationVariables(values) })
    }
  }

  const [cancelTowingRecord, cancelQuery] = useMutation<CancelTowingRecordResponse>(CANCEL_TOWING_RECORD, {
    onCompleted: data => {
      const { cancelTowingRecord } = data
      if (cancelTowingRecord.__typename === 'CancelTowingRecordSuccess') {
        maybeStopTrackingRecord(cancelTowingRecord.towingRecord)
        setSuccessNotification(
          formatMessage(cancelMessage.cancel_success_title),
          formatMessage(cancelMessage.cancel_success_message)
        )

        if (path.includes('edit')) {
          historyPush(path)
          return
        }

        historyPush(`/towing-jobs`)

        return
      }
      setErrorNotifications({ data: cancelTowingRecord })
    },
  })

  const initialValues: CancelOrEndTowingRecordFormValues = {
    towingRecordId: towingRecord.id,
    cancelReason: '',
    withFee: false,
    jobEndedAt: { date: new Date(), time: (format(new Date(), 'HH:mm') as unknown) as Time },
    cancelLocation: { address: '', city: '', zipcode: '', coords: { lat: null, long: null } },
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values: CancelOrEndTowingRecordFormValues) => {
          // validate fields and selected buttons
          if (!selectedButton) {
            setErrorNotification('Valitse toiminto', 'valitse miten haluat lopettaa työn')
            return
          }

          // const requireCancelLocationCoordinates =
          //   towingRecord.type === TowingRecordType.sos && selectedButton === "stopAndReady" &&
          //   (!values.cancelLocation.coords.lat || !values.cancelLocation.coords.long)

          // if (requireCancelLocationCoordinates) {
          //   setErrorNotification('Virhe', 'Sijaintitiedot ovat pakollisia SOS-työlle')
          //   return
          // }

          await finishJob(values, selectedButton)
        }}
      >
        {({ setFieldValue, values }) => {
          const setValue = (field: string, value: any) => setFieldValue(field, value)
          const getValue = (fieldName: string) => getIn(values, fieldName)
          const setRouteLocation = (locationData: Maybe<GeoLocateAddressData>) => {
            if (locationData) {
              setValue('cancelLocation.address', locationData.address)
              setValue('cancelLocation.city', locationData.city)
              setValue('cancelLocation.coords.lat', locationData.coords.lat)
              setValue('cancelLocation.coords.long', locationData.coords.long)
              setValue('cancelLocation.zipcode', locationData.zipcode)
            }
          }

          return (
            <RouteForm>
              <RouteWrapper>
                <RouteHeader
                  label={subFormType === 'cancel' ? `Peruuta työ` : `Työ valmis`}
                  regNo={towingRecord.vehicleDetails.registrationNumber}
                  cancel={onCancel}
                />
                <Styling padding="0 2rem" paddingMobile="0 0.5rem">
                  {subFormType === 'end' ? (
                    paymentLinkAlreadyActive ? (
                      <>
                        <BigButtonSection>
                          <Button
                            category="on"
                            secondaryCategory="yellow"
                            selectButton
                            selected={selectedButton === 'stopAndReady'}
                            label={formatMessage(cancelMessage.markComplete)}
                            onClick={() => {
                              setFieldValue('withFee', false)
                              setSelectedButton('stopAndReady')
                            }}
                            size="m"
                            fontWeight="normal"
                            maxWidth="100%"
                            dataTestId="stop-and-ready-button"
                          />
                          <Button
                            category="on"
                            secondaryCategory="yellow"
                            selectButton
                            selected={selectedButton === 'stopAndModify'}
                            label={formatMessage(cancelMessage.editWaybill)}
                            onClick={() => {
                              setFieldValue('withFee', false)
                              setSelectedButton('stopAndModify')
                            }}
                            size="m"
                            fontWeight="normal"
                            maxWidth="100%"
                          />
                        </BigButtonSection>
                      </>
                    ) : (
                      <>
                        <BigButtonSection>
                          <Button
                            category="on"
                            secondaryCategory="yellow"
                            selectButton
                            selected={selectedButton === 'stopAndReady'}
                            label="Siirrä laskutettavaksi"
                            onClick={() => {
                              setFieldValue('withFee', false)
                              setSelectedButton('stopAndReady')
                            }}
                            size="m"
                            fontWeight="normal"
                            maxWidth="100%"
                            dataTestId="stop-and-ready-button"
                          />
                          <Button
                            category="on"
                            secondaryCategory="yellow"
                            selectButton
                            selected={selectedButton === 'stopAndCard'}
                            label="Merkitse korttimaksuksi"
                            onClick={() => {
                              setFieldValue('withFee', false)
                              setSelectedButton('stopAndCard')
                            }}
                            size="m"
                            fontWeight="normal"
                            maxWidth="100%"
                          />
                        </BigButtonSection>
                        <BigButtonSection>
                          <Button
                            category="on"
                            secondaryCategory="yellow"
                            selectButton
                            selected={selectedButton === 'stopAndCash'}
                            label="Merkitse käteismaksuksi"
                            onClick={() => {
                              setFieldValue('withFee', false)
                              setSelectedButton('stopAndCash')
                            }}
                            size="m"
                            fontWeight="normal"
                            maxWidth="100%"
                          />
                          <Button
                            category="on"
                            secondaryCategory="yellow"
                            selectButton
                            selected={selectedButton === 'stopAndPaymentLink'}
                            label={formatMessage(cancelMessage.sendPaymentLink)}
                            onClick={() => {
                              setFieldValue('withFee', false)
                              setSelectedButton('stopAndPaymentLink')
                            }}
                            size="m"
                            fontWeight="normal"
                            maxWidth="100%"
                            disabled={!paymentLinkEnabled}
                            dataTestId="send-payment-link-button"
                          />
                        </BigButtonSection>
                        <BigButtonSection>
                          <Button
                            category="on"
                            secondaryCategory="yellow"
                            selectButton
                            selected={selectedButton === 'stopAndModify'}
                            label={formatMessage(cancelMessage.editWaybill)}
                            onClick={() => {
                              setFieldValue('withFee', false)
                              setSelectedButton('stopAndModify')
                            }}
                            size="m"
                            fontWeight="normal"
                            maxWidth="100%"
                          />
                        </BigButtonSection>
                      </>
                    )
                  ) : (
                    <>
                      <BigButtonSection>
                        <Button
                          category="on"
                          secondaryCategory="yellow"
                          selectButton
                          selected={selectedButton === 'stopAndReady'}
                          label="Siirrä laskutettavaksi"
                          onClick={() => {
                            setFieldValue('withFee', true)
                            setSelectedButton('stopAndReady')
                          }}
                          size="m"
                          fontWeight="normal"
                          maxWidth="100%"
                        />
                        <Button
                          category="on"
                          secondaryCategory="yellow"
                          selectButton
                          selected={selectedButton === 'stopAndCard'}
                          label="Merkitse korttimaksuksi"
                          onClick={() => {
                            setFieldValue('withFee', true)
                            setSelectedButton('stopAndCard')
                          }}
                          size="m"
                          fontWeight="normal"
                          maxWidth="100%"
                        />
                      </BigButtonSection>
                      <BigButtonSection>
                        <Button
                          category="on"
                          secondaryCategory="yellow"
                          selectButton
                          selected={selectedButton === 'stopAndCash'}
                          label="Merkitse käteismaksuksi"
                          onClick={() => {
                            setFieldValue('withFee', true)
                            setSelectedButton('stopAndCash')
                          }}
                          size="m"
                          fontWeight="normal"
                          maxWidth="100%"
                        />
                        <Button
                          category="on"
                          secondaryCategory="yellow"
                          selectButton
                          selected={selectedButton === 'stopAndCancel'}
                          label="Poista kuormakirja"
                          onClick={() => {
                            setFieldValue('withFee', false)
                            setSelectedButton('stopAndCancel')
                          }}
                          size="m"
                          fontWeight="normal"
                          maxWidth="100%"
                        />
                      </BigButtonSection>
                    </>
                  )}
                  {towingRecord.type === TowingRecordType.sos ? (
                    <RouteRow columns={'1fr 1fr'}>
                      <RouteColumn>
                        <DateTimeInput label="Työ lopetettu" name="jobEndedAt" required />
                      </RouteColumn>
                      <RouteColumn />
                    </RouteRow>
                  ) : null}
                  {subFormType === 'cancel' ? (
                    <>
                      <GetGeolocation
                        {...({
                          client,
                          setError: setErrorNotification,
                          type: GeolocationType.reverseGeocode,
                          reportGeolocation: setRouteLocation,
                          fieldName: 'cancelLocation',
                        } as any)}
                      />
                      <RouteRow columns={'1fr'}>
                        <RouteColumn>
                          <LocationInputMap
                            required={towingRecord.type === TowingRecordType.sos}
                            addressLabel="Osoite"
                            addressName="cancelLocation.address"
                            cityLabel="Kaupunki"
                            cityName="cancelLocation.city"
                            coordsName="cancelLocation.coords"
                            zipcodeName="cancelLocation.zipcode"
                            getValue={getValue}
                            values={toMapData(
                              getValue('cancelLocation.address'),
                              getValue('cancelLocation.city'),
                              getValue('cancelLocation.coords'),
                              getValue('cancelLocation.zipcode')
                            )}
                            setValue={setValue}
                            setRouteLocation={setRouteLocation}
                            stationId={null}
                          />
                        </RouteColumn>
                      </RouteRow>
                      <RouteRow columns={'1fr'}>
                        <RouteColumn>
                          <TextAreaInput label="Peruutuksen syy" name="cancelReason" required />
                        </RouteColumn>
                      </RouteRow>
                    </>
                  ) : null}
                  <ButtonContainer floatAtBottom>
                    <Button
                      label="Tallenna"
                      size="l"
                      category="save"
                      submit
                      maxWidth="100%"
                      disabled={endQuery.loading || cancelQuery.loading}
                      dataTestId="save-end-button"
                    />
                  </ButtonContainer>
                </Styling>
                <Loading loading={endQuery.loading || cancelQuery.loading} />
              </RouteWrapper>
            </RouteForm>
          )
        }}
      </Formik>
    </>
  )
}

export const CancelOrEndTowingRecord = CancelOrEndTowingRecordIntl
